import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QuoteIcon } from 'lucide-react';
interface Testimonial {
  quote: string;
  name: string;
  title: string;
  company: string;
}
const TESTIMONIALS: Testimonial[] = [{
  quote: 'Keystonegames cut our iteration cycle from 14 weeks to 3. The AI found patterns in player data we never would have seen.',
  name: 'Elena Vasiliou',
  title: 'CPO',
  company: 'Meridian Gaming'
}, {
  quote: 'We launched two new slot mechanics in Q1 alone. Their behavioral simulation was eerily accurate.',
  name: 'Marcus Lindberg',
  title: 'Head of Product',
  company: 'NordicBet Studios'
}, {
  quote: 'The ROI was clear within the first month. Our retention curves shifted dramatically.',
  name: 'Dmitri Kozlov',
  title: 'CTO',
  company: 'Aegean Interactive'
}];
export function Testimonials() {
  const [current, setCurrent] = useState(0);
  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % TESTIMONIALS.length);
  }, []);
  useEffect(() => {
    const interval = setInterval(next, 5000);
    return () => clearInterval(interval);
  }, [next]);
  const testimonial = TESTIMONIALS[current];
  return <section className="relative w-full py-32 lg:py-40 overflow-hidden bg-midnight-deep" aria-labelledby="testimonials-heading" data-id="element-177">
      {/* Background accent */}
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-amber/[0.02] blur-[100px]" data-id="element-178" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8" data-id="element-179">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-180">
          <p className="font-sora text-amber text-sm font-semibold tracking-[0.2em] uppercase mb-4" data-id="element-181">
            Trusted By Leaders
          </p>
          <h2 id="testimonials-heading" className="font-sora text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-id="element-182">
            What Studios Say
          </h2>
        </motion.div>

        {/* Quote area */}
        <motion.div className="relative text-center" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-183">
          {/* Decorative quote mark */}
          <QuoteIcon className="w-16 h-16 text-amber/15 mx-auto mb-8" aria-hidden="true" data-id="element-184" />

          {/* Animated quote */}
          <div className="min-h-[200px] flex flex-col items-center justify-center" data-id="element-185">
            <AnimatePresence mode="wait" data-id="element-186">
              <motion.div key={current} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1]
            }} className="flex flex-col items-center" data-id="element-187">
                <blockquote className="font-inter text-xl lg:text-2xl text-white leading-relaxed mb-8 max-w-3xl italic" data-id="element-188">
                  "{testimonial.quote}"
                </blockquote>

                <div className="flex flex-col items-center" data-id="element-189">
                  <p className="font-sora font-semibold text-white text-lg" data-id="element-190">
                    {testimonial.name}
                  </p>
                  <p className="font-inter text-text-muted text-sm mt-1" data-id="element-191">
                    {testimonial.title}, {testimonial.company}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation dots */}
          <div className="flex items-center justify-center gap-3 mt-10" role="tablist" aria-label="Testimonial navigation" data-id="element-192">
            {TESTIMONIALS.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`relative w-3 h-3 rounded-full transition-all duration-300 ${i === current ? 'bg-amber' : 'bg-white/20 hover:bg-white/40'}`} role="tab" aria-selected={i === current} aria-label={`Show testimonial ${i + 1}`} data-id="element-193">
                {i === current && <motion.div className="absolute inset-0 rounded-full bg-amber" layoutId="testimonial-dot" transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} data-id="element-194" />}
              </button>)}
          </div>
        </motion.div>
      </div>
    </section>;
}